import { useState, useEffect, useContext } from "react";
import {
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableSortLabel,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  Tabs,
  Tab,
  Toolbar,
  IconButton,
  TextField,
  Box,
  InputAdornment,
  Checkbox,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import { getQuotasStatistic, downloadQuotasStatistic, getTenantAllData } from "api/layer";
import { AppContext, AppAction } from "helpers/context";
import styles from "./ananlysisQuotas.module.scss";
import StartTimeDropdown from "./start-time-dropdown/start-time-dropdown";
import EndTimeDropdown from "./end-time-dropdown/end-time-dropdown";
import TenantDropdown from "./tenant-dropdown/tenant-dropdown";
import dayjs from "dayjs";

const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: 1,
});

function AnalysisStatistics() {
  const { t } = useTranslation();
  const [context, dispatch] = useContext(AppContext);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [analysisStatisticsData, setAnalysisStatisticsData] = useState<any[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [totalStatistic, setTotalStatistic] = useState<any>({});
  const [isLoading, setIsLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [tenantData, setTenantData] = useState([]);
  const [tenantDropdown, setTenantDropdown] = useState<any[]>([]);
  const [searchIpt, setSearchIpt] = useState("");
  const [searchData, setSearchData] = useState<{
    keyword: string;
    startOccurTime: any;
    endOccurTime: any;
    tenantIds: string;
  }>({
    keyword: "",
    startOccurTime: null,
    endOccurTime: null,
    tenantIds: ""
  });

  const [sortByField, setSortByField] = useState("tenant_name");
  const [sortOrder, setSortOrder] = useState<{ [key: string]: "asc" | "desc" }>({
    tenant_name: "asc",
    role_name: "asc",
    start_date: "asc",
    end_date: "asc",
    item_fathom_quotas: "asc",
    tr_item_fathom_count: "asc",
    item_quotas: "asc",
    tr_item_count: "asc",
    item_crs_quotas: "asc",
    tr_item_crs_count: "asc",
    linear_item_fathom_quotas: "asc",
    tr_polyline_fathom_item_count: "asc",
    linear_item_quotas: "asc",
    tr_polyline_item_count: "asc",
    zonal_item_fathom_quotas: "asc",
    tr_polygon_fathom_item_count: "asc",
    zonal_item_quotas: "asc",
    tr_polygon_item_count: "asc",
  });

  const hanldeDownload = () => {
    const filteredData = Object.fromEntries(
      Object.entries(searchData).filter(([key, value]) => value !== null && value !== '')
    );
    if (filteredData.startOccurTime) {
      filteredData.startOccurTime = filteredData.startOccurTime.format("YYYY-MM-DD");
    }
    if (filteredData.endOccurTime) {
      filteredData.endOccurTime = filteredData.endOccurTime.format("YYYY-MM-DD");
    }
    downloadQuotasStatistic({ ...filteredData, language: context.language }).then((res: any) => {
      const url = window.URL.createObjectURL(new Blob([res as any], { type: "application/force-download;charset=utf-8" }));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `${t("userSystem.analysisStatisticsDownload")}.xls`);
      document.body.appendChild(link);
      link.click();
      link.onload = () => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      };
    });
  };

  const toggleFilters = () => {
    setShowFilters(!showFilters);
  };

  const handleSearch = (params: any) => {
    setSearchData((prevData) => ({ ...prevData, [params.name]: params.value }));
    if ((params.name === "startOccurTime" || params.name === "endOccurTime") && params.value && params.value.format("YYYY-MM-DD") === "Invalid Date") {
      return;
    }
    setPage(0);
    handleGetAnalysisStatistics({ pageNo: 1, search: { ...searchData, [params.name]: params.value } });
  };

  const handleKeyDown = (event: any) => {
    if (event.key === "Enter") {
      handleSearch({ name: "keyword", value: event.target.value });
    }
  };

  const handleTenantDropdown = (value: any) => {
    setTenantDropdown(value);
    handleSearch({ name: "tenantIds", value: value.map((item: any) => item.id).join(",") });
  };

  const handleChangePage = (event: any, newPage: number) => {
    setPage(newPage);
    handleGetAnalysisStatistics({ pageNo: newPage + 1 });
  };

  const handleChangeRowsPerPage = (event: any) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
    handleGetAnalysisStatistics({ pageNo: 0, pageSize: parseInt(event.target.value, 10) });
  };

  const handleGetAnalysisStatistics = async ({
    pageNo = page + 1,
    pageSize = rowsPerPage,
    sortOrderAsc = sortOrder[sortByField] === "asc",
    sortByFields = sortByField,
    search = searchData } = {}) => {
    setIsLoading(true);
    let params = { ...search };
    if (search.startOccurTime) {
      params.startOccurTime = search.startOccurTime.format("YYYY-MM-DD");
    }
    if (search.endOccurTime) {
      params.endOccurTime = search.endOccurTime.format("YYYY-MM-DD");
    }
    const res: any = await getQuotasStatistic({ ...params, pageNo, pageSize, sortOrderAsc, sortByField: sortByFields, });
    // const res: any = await getQuotasStatistic({ ...params, pageNo, pageSize });
    setIsLoading(false);
    if (res.code !== 200) return;
    setAnalysisStatisticsData(res.data.rows);
    setTotalCount(res.data.totalCount * 1 || 0);
    setTotalStatistic(res.data.totalStatistic)
  };

  const handleGetTenantData = async () => {
    const res: any = await getTenantAllData();
    if (res.code !== 200) {
      return;
    }
    setTenantData(res.data);
  };

  useEffect(() => {
    handleGetTenantData();
    handleGetAnalysisStatistics();
  }, []);

  return (
    <div className={styles.container}>
      <Toolbar style={{ justifyContent: "space-between", borderBottom: "1px solid #ccc", padding: "0" }}>
        <div>
          <Button
            size="small"
            variant="outlined"
            color="success"
            style={{ padding: "4px 10px", marginRight: "10px" }}
            onClick={hanldeDownload}
          >
            {t("buttons.download")}
          </Button>
        </div>

        <IconButton
          color="success"
          onClick={toggleFilters}
          style={{ color: showFilters ? "#2e7d32" : "rgba(0, 0, 0, 0.54)" }}
        >
          <span className="material-icons">filter_list</span>
        </IconButton>
      </Toolbar>

      {showFilters && (
        <Box style={{ display: "flex", alignItems: "center", padding: "8px", backgroundColor: "#f5f5f5", justifyContent: "space-between" }}>
          <Box style={{ display: "flex", flex: 1, alignItems: "center" }}>
            <TextField
              color="success"
              variant="outlined"
              size="small"
              placeholder={t("userSystem.filterByKeyword")}
              value={searchIpt}
              onChange={(e) => setSearchIpt(e.target.value)}
              onKeyDown={handleKeyDown}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <span className="material-icons">filter_list</span>
                  </InputAdornment>
                ),
                endAdornment: searchIpt && (
                  <InputAdornment position="end">
                    <span
                      className="material-icons cursor-pointer"
                      onClick={() => {
                        setSearchIpt("");
                        searchData.keyword && handleSearch({ name: "keyword", value: "" });
                      }}
                    >
                      close
                    </span>
                  </InputAdornment>
                ),
              }}
              sx={{
                width: "250px",
                "& .MuiOutlinedInput-notchedOutline": {
                  borderWidth: "0",
                },
                "& .MuiOutlinedInput-input,& .MuiOutlinedInput-input::placeholder": {
                  fontSize: "14px",
                },
              }}
            />
            <TenantDropdown
              tenantData={tenantData}
              selectedTenants={tenantDropdown}
              onSave={(value) => handleTenantDropdown(value)}
            />
            <StartTimeDropdown
              defaultTime={searchData.startOccurTime}
              onSave={(value) => handleSearch({ name: "startOccurTime", value })}
            />
            <EndTimeDropdown
              defaultTime={searchData.endOccurTime}
              onSave={(value) => handleSearch({ name: "endOccurTime", value })}
            />
          </Box>
          <IconButton
            onClick={() => setShowFilters(false)}
            aria-label="clear"
            color="default"
          >
            <span className="material-icons">clear</span>
          </IconButton>
        </Box>
      )}

      <div style={{ flex: 1, overflow: "auto" }}>
        <Table
          stickyHeader
          aria-label="sticky  table"
          size="small"
          style={{
            tableLayout: "fixed",
            width: "100%",
            minWidth: "100%"
          }}
        >
          <TableHead>
            <TableRow>
              <TableCell style={{
                width: 140,
                whiteSpace: "nowrap",
                padding: "6px"
              }}>
                <TableSortLabel
                  active={sortByField === "tenant_name"}
                  onClick={() => {
                    handleGetAnalysisStatistics({ sortOrderAsc: !(sortOrder.tenant_name === "asc"), sortByFields: "tenant_name" })
                    setSortByField("tenant_name")
                    let sort: any = {
                      ...sortOrder,
                      tenant_name: sortOrder.tenant_name === "asc" ? "desc" : "asc"
                    }
                    setSortOrder(sort)
                  }}
                  direction={sortOrder.tenant_name}
                >
                  {t("userSystem.tenantName")}
                </TableSortLabel>
              </TableCell>
              <TableCell style={{
                width: 120,
                whiteSpace: "nowrap",
                padding: "6px"
              }}>
                <TableSortLabel
                  active={sortByField === "role_name"}
                  onClick={() => {
                    handleGetAnalysisStatistics({ sortOrderAsc: !(sortOrder.role_name === "asc"), sortByFields: "role_name" })
                    setSortByField("role_name")
                    let sort: any = {
                      ...sortOrder,
                      role_name: sortOrder.role_name === "asc" ? "desc" : "asc"
                    }
                    setSortOrder(sort)
                  }}
                  direction={sortOrder.role_name}
                >
                  {t("userSystem.roleName")}
                </TableSortLabel>
              </TableCell>
              <TableCell style={{
                width: 120,
                whiteSpace: "nowrap",
                padding: "6px"
              }}>
                <TableSortLabel
                  active={sortByField === "start_date"}
                  onClick={() => {
                    handleGetAnalysisStatistics({ sortOrderAsc: !(sortOrder.start_date === "asc"), sortByFields: "start_date" })
                    setSortByField("start_date")
                    let sort: any = {
                      ...sortOrder,
                      start_date: sortOrder.start_date === "asc" ? "desc" : "asc"
                    }
                    setSortOrder(sort)
                  }}
                  direction={sortOrder.start_date}
                >

                  {t("userSystem.startDate")}
                </TableSortLabel>
              </TableCell>
              <TableCell style={{
                width: 120,
                whiteSpace: "nowrap",
                padding: "6px"
              }}>
                <TableSortLabel
                  active={sortByField === "end_date"}
                  onClick={() => {
                    handleGetAnalysisStatistics({ sortOrderAsc: !(sortOrder.end_date === "asc"), sortByFields: "end_date" })
                    setSortByField("end_date")
                    let sort: any = {
                      ...sortOrder,
                      end_date: sortOrder.end_date === "asc" ? "desc" : "asc"
                    }
                    setSortOrder(sort)
                  }}
                  direction={sortOrder.end_date}
                >

                  {t("userSystem.endDate")}
                </TableSortLabel>
              </TableCell>
              <TableCell style={{
                padding: "6px",
                width: "150px",
                backgroundColor: "rgba(0, 0, 0, 0.08)"
              }}>
                <TableSortLabel
                  active={sortByField === "item_fathom_quotas"}
                  onClick={() => {
                    handleGetAnalysisStatistics({ sortOrderAsc: !(sortOrder.item_fathom_quotas === "asc"), sortByFields: "item_fathom_quotas" })
                    setSortByField("item_fathom_quotas")
                    let sort: any = {
                      ...sortOrder,
                      item_fathom_quotas: sortOrder.item_fathom_quotas === "asc" ? "desc" : "asc"
                    }
                    setSortOrder(sort)
                  }}
                  direction={sortOrder.item_fathom_quotas}
                >

                  {t("userSystem.itemFathomQuotas")}
                </TableSortLabel>
              </TableCell>
              <TableCell style={{
                padding: "6px",
                width: "150px"
              }}>
                <TableSortLabel
                  active={sortByField === "tr_item_fathom_count"}
                  onClick={() => {
                    handleGetAnalysisStatistics({ sortOrderAsc: !(sortOrder.tr_item_fathom_count === "asc"), sortByFields: "tr_item_fathom_count" })
                    setSortByField("tr_item_fathom_count")
                    let sort: any = {
                      ...sortOrder,
                      tr_item_fathom_count: sortOrder.tr_item_fathom_count === "asc" ? "desc" : "asc"
                    }
                    setSortOrder(sort)
                  }}
                  direction={sortOrder.tr_item_fathom_count}
                >

                  {t("userSystem.trItemFathomCount")}
                </TableSortLabel>
              </TableCell>
              <TableCell style={{
                padding: "6px",
                width: "150px",
                backgroundColor: "rgba(0, 0, 0, 0.08)"
              }}>
                <TableSortLabel
                  active={sortByField === "item_quotas"}
                  onClick={() => {
                    handleGetAnalysisStatistics({ sortOrderAsc: !(sortOrder.item_quotas === "asc"), sortByFields: "item_quotas" })
                    setSortByField("item_quotas")
                    let sort: any = {
                      ...sortOrder,
                      item_quotas: sortOrder.item_quotas === "asc" ? "desc" : "asc"
                    }
                    setSortOrder(sort)
                  }}
                  direction={sortOrder.item_quotas}
                >

                  {t("userSystem.itemQuotas")}
                </TableSortLabel>
              </TableCell>
              <TableCell style={{
                padding: "6px",
                width: "150px"
              }}>
                <TableSortLabel
                  active={sortByField === "tr_item_count"}
                  onClick={() => {
                    handleGetAnalysisStatistics({ sortOrderAsc: !(sortOrder.tr_item_count === "asc"), sortByFields: "tr_item_count" })
                    setSortByField("tr_item_count")
                    let sort: any = {
                      ...sortOrder,
                      tr_item_count: sortOrder.tr_item_count === "asc" ? "desc" : "asc"
                    }
                    setSortOrder(sort)
                  }}
                  direction={sortOrder.tr_item_count}
                >

                  {t("userSystem.trItemCount")}
                </TableSortLabel>
              </TableCell>
              <TableCell style={{
                padding: "6px",
                width: "150px",
                backgroundColor: "rgba(0, 0, 0, 0.08)"
              }}>
                <TableSortLabel
                  active={sortByField === "item_crs_quotas"}
                  onClick={() => {
                    handleGetAnalysisStatistics({ sortOrderAsc: !(sortOrder.item_crs_quotas === "asc"), sortByFields: "item_crs_quotas" })
                    setSortByField("item_crs_quotas")
                    let sort: any = {
                      ...sortOrder,
                      item_crs_quotas: sortOrder.item_crs_quotas === "asc" ? "desc" : "asc"
                    }
                    setSortOrder(sort)
                  }}
                  direction={sortOrder.item_crs_quotas}
                >

                  {t("userSystem.itemCrsQuotas")}
                </TableSortLabel>
              </TableCell>
              <TableCell style={{
                padding: "6px",
                width: "150px"
              }}>
                <TableSortLabel
                  active={sortByField === "tr_item_crs_count"}
                  onClick={() => {
                    handleGetAnalysisStatistics({ sortOrderAsc: !(sortOrder.tr_item_crs_count === "asc"), sortByFields: "tr_item_crs_count" })
                    setSortByField("tr_item_crs_count")
                    let sort: any = {
                      ...sortOrder,
                      tr_item_crs_count: sortOrder.tr_item_crs_count === "asc" ? "desc" : "asc"
                    }
                    setSortOrder(sort)
                  }}
                  direction={sortOrder.tr_item_crs_count}
                >

                  {t("userSystem.trItemCrsCount")}
                </TableSortLabel>
              </TableCell>
              <TableCell style={{
                padding: "6px",
                width: "150px",
                backgroundColor: "rgba(0, 0, 0, 0.08)"
              }}>
                <TableSortLabel
                  active={sortByField === "linear_item_fathom_quotas"}
                  onClick={() => {
                    handleGetAnalysisStatistics({ sortOrderAsc: !(sortOrder.linear_item_fathom_quotas === "asc"), sortByFields: "linear_item_fathom_quotas" })
                    setSortByField("linear_item_fathom_quotas")
                    let sort: any = {
                      ...sortOrder,
                      linear_item_fathom_quotas: sortOrder.linear_item_fathom_quotas === "asc" ? "desc" : "asc"
                    }
                    setSortOrder(sort)
                  }}
                  direction={sortOrder.linear_item_fathom_quotas}
                >

                  {t("userSystem.linearItemFathomQuotas")}
                </TableSortLabel>
              </TableCell>
              <TableCell style={{
                padding: "6px",
                width: "150px"
              }}>
                <TableSortLabel
                  active={sortByField === "tr_polyline_fathom_item_count"}
                  onClick={() => {
                    handleGetAnalysisStatistics({ sortOrderAsc: !(sortOrder.tr_polyline_fathom_item_count === "asc"), sortByFields: "tr_polyline_fathom_item_count" })
                    setSortByField("tr_polyline_fathom_item_count")
                    let sort: any = {
                      ...sortOrder,
                      tr_polyline_fathom_item_count: sortOrder.tr_polyline_fathom_item_count === "asc" ? "desc" : "asc"
                    }
                    setSortOrder(sort)
                  }}
                  direction={sortOrder.tr_polyline_fathom_item_count}
                >

                  {t("userSystem.trPolylineFathomItemCount")}
                </TableSortLabel>
              </TableCell>
              <TableCell style={{
                padding: "6px",
                width: "150px",
                backgroundColor: "rgba(0, 0, 0, 0.08)"
              }}>
                <TableSortLabel
                  active={sortByField === "linear_item_quotas"}
                  onClick={() => {
                    handleGetAnalysisStatistics({ sortOrderAsc: !(sortOrder.linear_item_quotas === "asc"), sortByFields: "linear_item_quotas" })
                    setSortByField("linear_item_quotas")
                    let sort: any = {
                      ...sortOrder,
                      linear_item_quotas: sortOrder.linear_item_quotas === "asc" ? "desc" : "asc"
                    }
                    setSortOrder(sort)
                  }}
                  direction={sortOrder.linear_item_quotas}
                >

                  {t("userSystem.linearItemQuotas")}
                </TableSortLabel>
              </TableCell>
              <TableCell style={{
                padding: "6px",
                width: "150px"
              }}>
                <TableSortLabel
                  active={sortByField === "tr_polyline_item_count"}
                  onClick={() => {
                    handleGetAnalysisStatistics({ sortOrderAsc: !(sortOrder.tr_polyline_item_count === "asc"), sortByFields: "tr_polyline_item_count" })
                    setSortByField("tr_polyline_item_count")
                    let sort: any = {
                      ...sortOrder,
                      tr_polyline_item_count: sortOrder.tr_polyline_item_count === "asc" ? "desc" : "asc"
                    }
                    setSortOrder(sort)
                  }}
                  direction={sortOrder.tr_polyline_item_count}
                >

                  {t("userSystem.trPolylineItemCount")}
                </TableSortLabel>
              </TableCell>
              <TableCell style={{
                padding: "6px",
                width: "150px",
                backgroundColor: "rgba(0, 0, 0, 0.08)"
              }}>
                <TableSortLabel
                  active={sortByField === "zonal_item_fathom_quotas"}
                  onClick={() => {
                    handleGetAnalysisStatistics({ sortOrderAsc: !(sortOrder.zonal_item_fathom_quotas === "asc"), sortByFields: "zonal_item_fathom_quotas" })
                    setSortByField("zonal_item_fathom_quotas")
                    let sort: any = {
                      ...sortOrder,
                      zonal_item_fathom_quotas: sortOrder.zonal_item_fathom_quotas === "asc" ? "desc" : "asc"
                    }
                    setSortOrder(sort)
                  }}
                  direction={sortOrder.zonal_item_fathom_quotas}
                >

                  {t("userSystem.zonalItemFathomQuotas")}
                </TableSortLabel>
              </TableCell>
              <TableCell style={{
                padding: "6px",
                width: "150px"
              }}>
                <TableSortLabel
                  active={sortByField === "tr_polygon_fathom_item_count"}
                  onClick={() => {
                    handleGetAnalysisStatistics({ sortOrderAsc: !(sortOrder.tr_polygon_fathom_item_count === "asc"), sortByFields: "tr_polygon_fathom_item_count" })
                    setSortByField("tr_polygon_fathom_item_count")
                    let sort: any = {
                      ...sortOrder,
                      tr_polygon_fathom_item_count: sortOrder.tr_polygon_fathom_item_count === "asc" ? "desc" : "asc"
                    }
                    setSortOrder(sort)
                  }}
                  direction={sortOrder.tr_polygon_fathom_item_count}
                >
                  {t("userSystem.trPolygonFathomItemCount")}
                </TableSortLabel>
              </TableCell>
              <TableCell style={{
                padding: "6px",
                width: "150px",
                backgroundColor: "rgba(0, 0, 0, 0.08)"
              }}>
                <TableSortLabel
                  active={sortByField === "zonal_item_quotas"}
                  onClick={() => {
                    handleGetAnalysisStatistics({ sortOrderAsc: !(sortOrder.zonal_item_quotas === "asc"), sortByFields: "zonal_item_quotas" })
                    setSortByField("zonal_item_quotas")
                    let sort: any = {
                      ...sortOrder,
                      zonal_item_quotas: sortOrder.zonal_item_quotas === "asc" ? "desc" : "asc"
                    }
                    setSortOrder(sort)
                  }}
                  direction={sortOrder.zonal_item_quotas}
                >
                  {t("userSystem.zonalItemQuotas")}
                </TableSortLabel>
              </TableCell>
              <TableCell style={{
                padding: "6px",
                width: "150px"
              }}>
                <TableSortLabel
                  active={sortByField === "tr_polygon_item_count"}
                  onClick={() => {
                    handleGetAnalysisStatistics({ sortOrderAsc: !(sortOrder.tr_polygon_item_count === "asc"), sortByFields: "tr_polygon_item_count" })
                    setSortByField("tr_polygon_item_count")
                    let sort: any = {
                      ...sortOrder,
                      tr_polygon_item_count: sortOrder.tr_polygon_item_count === "asc" ? "desc" : "asc"
                    }
                    setSortOrder(sort)
                  }}
                  direction={sortOrder.tr_polygon_item_count}
                >
                  {t("userSystem.trPolygonItemCount")}
                </TableSortLabel>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={14}>
                  <div className={styles.loading}>
                    <div className="loading loading-spinner loading-lg"></div>
                    <div>{t("layers.loading")}</div>
                  </div>
                </TableCell>
              </TableRow>
            ) : (<>{
              analysisStatisticsData.map((item: any, index: number) => (
                <TableRow
                  key={item.tenant_name + item.role_name + index}
                  className={styles.row}
                >
                  <TableCell style={{ padding: "6px" }}>{item.tenant_name}</TableCell>
                  <TableCell style={{ padding: "6px" }}>{item.role_name}</TableCell>
                  <TableCell style={{ padding: "6px" }}>{item.start_date}</TableCell>
                  <TableCell style={{ padding: "6px" }}>{item.end_date}</TableCell>
                  <TableCell style={{ padding: "6px", backgroundColor: "rgba(0, 0, 0, 0.08)" }}>{item.item_fathom_quotas}</TableCell>
                  <TableCell style={{ padding: "6px" }}>{item.tr_item_fathom_count}</TableCell>
                  <TableCell style={{
                    padding: "6px",
                    backgroundColor: "rgba(0, 0, 0, 0.08)"
                  }}>{item.item_quotas}</TableCell>
                  <TableCell style={{ padding: "6px" }}>{item.tr_item_count}</TableCell>
                  <TableCell style={{
                    padding: "6px",
                    backgroundColor: "rgba(0, 0, 0, 0.08)"
                  }}>{item.item_crs_quotas}</TableCell>
                  <TableCell style={{ padding: "6px" }}>{item.tr_item_crs_count}</TableCell>
                  <TableCell style={{
                    padding: "6px",
                    backgroundColor: "rgba(0, 0, 0, 0.08)"
                  }}>{item.linear_item_fathom_quotas}</TableCell>
                  <TableCell style={{ padding: "6px" }}>{item.tr_polyline_fathom_item_count}</TableCell>
                  <TableCell style={{
                    padding: "6px",
                    backgroundColor: "rgba(0, 0, 0, 0.08)"
                  }}>{item.linear_item_quotas}</TableCell>
                  <TableCell style={{ padding: "6px" }}>{item.tr_polyline_item_count}</TableCell>
                  <TableCell style={{
                    padding: "6px",
                    backgroundColor: "rgba(0, 0, 0, 0.08)"
                  }}>{item.zonal_item_fathom_quotas}</TableCell>
                  <TableCell style={{ padding: "6px" }}>{item.tr_polygon_fathom_item_count}</TableCell>
                  <TableCell style={{
                    padding: "6px",
                    backgroundColor: "rgba(0, 0, 0, 0.08)"
                  }}>{item.zonal_item_quotas}</TableCell>
                  <TableCell style={{ padding: "6px" }}>{item.tr_polygon_item_count}</TableCell>
                </TableRow>
              ))}</>
            )}
          </TableBody>
        </Table>
      </div>
      <TablePagination
        rowsPerPageOptions={[5, 10, 25]}
        component="div"
        count={totalCount}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        labelRowsPerPage={t("userSystem.rowsPerPage")}
      />
    </div >
  );
}

export default AnalysisStatistics;
